import {
  emptyFilter,
  isEmptyFilter,
  validateFilter,
  type FieldDefinition,
  type FieldOption,
  type Filter,
  type Sort,
} from "@estelle/domain";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Download } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  applyColumns,
  decodeColumns,
  defaultColumns,
  readColumnPreference,
  writeColumnPreference,
  type ColumnState,
} from "@/components/columns/columns";
import { CollapsedFilters } from "@/components/filters/CollapsedFilters";
import { commonFilterFields } from "@/components/filters/common";
import { FilterBar } from "@/components/filters/FilterBar";
import {
  resolveLayout,
  writeLayoutPreference,
  type ListLayout,
} from "@/components/list/layout";
import { FilterProblemsContext } from "@/components/filters/problems";
import { decodeFilter, encodeFilter } from "@/components/filters/url";
import { EmptyState, NoAccess } from "@/components/shell/EmptyState";
import {
  ListStateContext,
  type ListState,
} from "@/components/shell/list-state";
import { PageContainer } from "@/components/shell/PageContainer";
import { SectionActionsContext } from "@/components/shell/section-actions";
import { ListToolsSlotContext } from "@/components/table/list-tools";
import { csvFilename, downloadCsv, toCsv } from "@/components/table/csv";
import { ResultSetContext } from "@/components/table/useCursorHistory";
import { decodeSort, encodeSort, toggleSort } from "@/components/table/sorting";
import { Button } from "@/components/ui/button";
import { useSavedViews } from "@/components/views/useSavedViews";
import { useDataSource } from "@/adapters/data-source";
import { useIsMobile } from "@/hooks/use-mobile";
import { reportingNow } from "@/lib/reportClock";
import { moduleUi } from "@/modules/registry";
import { useSectionSearch } from "@/routes/search";
import { sectionPermissions } from "@/config/modules";
import { useActiveSection } from "@/routes/useActiveSection";
import { can, usePermissions } from "@/auth/permissions";

const TABLE_ONLY: readonly ListLayout[] = ["table"];

/**
 * The layout in the URL. Omitted for the table, which is also what an absent
 * one falls back to once no preference is stored — so the common case leaves
 * links as short as they were.
 */
function encodeLayout(layout: ListLayout): string | undefined {
  return layout === "table" ? undefined : layout;
}

/**
 * The frame every section renders inside.
 *
 * It owns the list state — filter, sort, columns — so there is exactly one
 * place that knows how it is stored: filter and sort in the URL, because they
 * decide which rows a shared link shows; columns in this browser, because they
 * only decide how those rows look. A module supplies its fields and its screens; everything around
 * them is shared, which is what stops ten module lists from becoming ten
 * slightly different list implementations.
 */
export function SectionPage() {
  const { module, section } = useActiveSection();
  const navigate = useNavigate();
  const search = useSectionSearch();
  const data = useDataSource();
  const permissions = usePermissions();
  const isMobile = useIsMobile();
  const [exporting, setExporting] = useState(false);
  // Where the screen's own primary action goes, beside Export (#637).
  const [actionSlot, setActionSlot] = useState<HTMLElement | null>(null);
  // Where a phone list's Select goes, in the toolbar's tabs row (#647).
  const [toolsSlot, setToolsSlot] = useState<HTMLElement | null>(null);

  const sectionUi = moduleUi(module.id)?.sections[section.id];
  const Screen = sectionUi?.screen;

  /**
   * The vocabulary belongs to the section, not the module.
   *
   * Stock Levels and Adjustments live in one module and describe different
   * rows; so do Invoices and Credit Notes. Resolving the toolbar against a
   * module-wide list gave those sections a default column set that did not
   * describe them, and every column rendered as an em dash.
   *
   * Memoised because a fresh `[]` each render would make the filter and sort
   * decoders below recompute on the render path of every list.
   */
  const declaredFields = useMemo<FieldDefinition[]>(() => {
    // A field the user's permissions do not cover is not offered at all
    // (`SectionUi.fieldPermissions`) — the server withholds its values anyway.
    const needed = sectionUi?.fieldPermissions;
    return (sectionUi?.fields ?? []).filter((field) => {
      const permission = needed?.[field.name];
      return permission === undefined || can(permissions, permission);
    });
  }, [sectionUi, permissions]);

  /*
    Options that are data, not declaration — a store's collections (#308).

    Fetched through the data-access interface like anything else, then merged
    into the fields below, so the filter builder reads them off `options`
    exactly as it reads a status's. A failed or pending fetch leaves the
    field without options, which is a typed value rather than a picker and
    filters the same; it never takes the list down with it.
  */
  const loadFieldOptions = sectionUi?.fieldOptions;
  const fieldOptions = useQuery({
    queryKey: ["section", "field-options", module.id, section.id],
    queryFn: (): Promise<Record<string, FieldOption[]>> =>
      loadFieldOptions ? loadFieldOptions(data) : Promise.resolve({}),
    enabled: loadFieldOptions !== undefined,
  });
  const loadedOptions = fieldOptions.data;

  const allFields = useMemo<FieldDefinition[]>(() => {
    if (!loadedOptions) return declaredFields;
    return declaredFields.map((field) => {
      const options = loadedOptions[field.name];
      return options && options.length > 0 ? { ...field, options } : field;
    });
  }, [declaredFields, loadedOptions]);

  const filter = useMemo(() => decodeFilter(search.filter), [search.filter]);
  const sort = useMemo(
    () => decodeSort(search.sort, allFields),
    [search.sort, allFields],
  );

  /*
    Why the filter in force may match nothing, for the "Nothing matches" a list
    draws (#518): a date range that runs backwards, a range with one end empty.
    Said once here rather than in each of the lists that draw it.
  */
  const filterProblems = useMemo(
    () => [
      ...new Set(
        validateFilter(filter, allFields).map((problem) => problem.message),
      ),
    ],
    [filter, allFields],
  );

  const here = `${module.id}/${section.id}`;

  /*
    Which rows the list below is paging through: this section, under this
    filter, search and sort. A paged screen's cursor trail returns to page one
    when it changes (`useCursorHistory`, #458), so no screen has to notice.
    The raw URL strings, not the decoded values — equal links, equal rows.
  */
  const resultSet = JSON.stringify([
    here,
    search.filter,
    search.q,
    search.sort,
  ]);

  /*
    Saved views belong to a section, not the module. Scoped to the module,
    Orders' "Fulfilled, not invoiced" default was applied to Shipments and
    Recent Changes too, whose rows have neither field — every row was
    filtered out (2026-09-15). The first section keeps the bare module id so
    views saved before this still belong to the list they were made on.
  */
  const viewScope =
    module.sections[0]?.id === section.id
      ? module.id
      : `${module.id}.${section.id}`;
  const { defaultView } = useSavedViews(viewScope);

  /*
    Columns per list, from this browser: what was chosen here, else the
    scope's default view, else every field. Re-read during render when the
    section changes, rather than in an effect, so a new section never paints
    one frame with the previous section's column names.
  */
  const [columnState, setColumnState] = useState(() => ({
    scope: here,
    fields: allFields,
    stored: readColumnPreference(here, allFields),
  }));
  let stored = columnState.stored;
  if (columnState.scope !== here || columnState.fields !== allFields) {
    stored = readColumnPreference(here, allFields);
    setColumnState({ scope: here, fields: allFields, stored });
  }
  const columns =
    stored ?? decodeColumns(defaultView?.columns.join(","), allFields);

  function setColumns(next: ColumnState) {
    writeColumnPreference(here, next, allFields);
    setColumnState({ scope: here, fields: allFields, stored: next });
  }
  const layouts = sectionUi?.layouts ?? TABLE_ONLY;
  const layout = resolveLayout(search.view, here, layouts);

  /**
   * Every toolbar control writes the whole search object rather than patching
   * one key, so applying a filter cannot silently drop the sort, the search,
   * or each other. Columns go to storage instead of the URL.
   */
  function apply(next: {
    filter?: Filter;
    columns?: ColumnState;
    sort?: Sort[];
    /** `null` clears it; omitted leaves it as it is. */
    q?: string | null;
    view?: ListLayout;
  }) {
    if (next.columns) setColumns(next.columns);
    void navigate({
      to: ".",
      search: {
        filter: encodeFilter(next.filter ?? filter),
        sort: encodeSort(next.sort ?? sort),
        q: next.q === null ? undefined : (next.q ?? search.q),
        view: encodeLayout(next.view ?? layout),
        // A report's chart settings are not list state, and changing the
        // filter must not reset them.
        chart: search.chart,
      },
    });
  }

  const hasFields = allFields.length > 0;
  // The first permission the section needs that this user lacks, if any.
  const missingPermission = sectionPermissions(section).find(
    (permission) => !can(permissions, permission),
  );
  const subject = sectionUi?.subject ?? module.label.toLowerCase();

  /*
    The filters this list is usually narrowed by (#633), and how many rows it
    holds, beside the title and on the phone's "Show 78 orders". The count is
    the section's own (`SectionUi.count`), with its standing filter in it.
  */
  const common = useMemo(
    () => commonFilterFields(allFields, sectionUi?.commonFilters),
    [allFields, sectionUi?.commonFilters],
  );
  const countFn = sectionUi?.count;
  const countQuery = useQuery({
    queryKey: ["section", "count", here, search.filter, search.q],
    queryFn: () =>
      countFn ? countFn(data, { filter, search: search.q }) : null,
    enabled: countFn !== undefined && missingPermission === undefined,
    staleTime: 15_000,
  });
  const resultCount = countQuery.data ?? null;
  const countOf = useMemo(
    () =>
      countFn
        ? (viewFilter: Filter) => countFn(data, { filter: viewFilter })
        : undefined,
    [countFn, data],
  );

  /*
    Whether an empty list is empty or filtered-empty (#642): the person's own
    filter or typed search, not the section's standing one, which they cannot
    clear.
  */
  const listState = useMemo<ListState>(
    () => ({
      narrowed: !isEmptyFilter(filter) || (search.q ?? "").trim() !== "",
      things: (
        sectionUi?.subject ?? section.label.replace(/^(All|My) /, "")
      ).toLowerCase(),
      nothingYet: sectionUi?.nothingYet,
    }),
    [
      filter,
      search.q,
      section.label,
      sectionUi?.subject,
      sectionUi?.nothingYet,
    ],
  );

  /**
   * Apply the scope's default view when the section is opened cold.
   *
   * `useSavedViews` resolves a default and the picker renders its name, but
   * nothing ever put its filter, columns and sort into the URL — so the label
   * was the only part of a default view that worked, and a list showing every
   * order sat under the name of one that does not.
   *
   * Only when the URL carries no list state. A link someone was sent is
   * explicit and beats a default; silently narrowing it would mean two people
   * opening the same link see different rows.
   *
   * Once per entry, not once per empty URL. Clearing the filter has to leave
   * an unfiltered list — a Clear that bounced straight back to the default
   * would look like it had done nothing at all — and the three encoders all
   * return `undefined` at their defaults, so re-running on an empty URL would
   * also loop for a default view that happens to equal them.
   */
  const appliedFor = useRef<string | null>(null);
  const untouched = search.filter === undefined && search.sort === undefined;

  useEffect(() => {
    if (appliedFor.current === here) return;
    if (!hasFields || !defaultView) return;
    appliedFor.current = here;
    if (!untouched) return;

    void navigate({
      to: ".",
      replace: true,
      search: () => ({
        view: search.view,
        chart: search.chart,
        filter: encodeFilter(defaultView.filter),
        sort: encodeSort(defaultView.sort),
      }),
    });
  }, [
    here,
    hasFields,
    defaultView,
    untouched,
    allFields,
    navigate,
    search.view,
    search.chart,
  ]);

  /**
   * Exports the current filter, search and columns, not the visible page.
   *
   * Someone who filters to three hundred orders and receives twenty-five has
   * been handed the wrong answer without being told — so this follows the
   * cursor to the end, and writes the columns in the order the user put them
   * in rather than the module's declaration order.
   *
   * The typed search is part of what the list is showing, so it is part of
   * what is exported: it was left out, and a file of every stock level came
   * from a screen that showed three (#521).
   */
  async function exportCsv() {
    const rows = sectionUi?.exportRows;
    if (!rows) return;

    setExporting(true);
    try {
      const projected = await rows(
        data,
        { filter, sort, search: search.q },
        { permissions },
      );
      const csv = Array.isArray(projected)
        ? toCsv(applyColumns(allFields, columns), projected)
        : toCsv(projected.columns, projected.rows);
      downloadCsv(
        csvFilename(
          module.id,
          section.id,
          new Date(reportingNow()).toISOString(),
        ),
        csv,
      );
    } finally {
      setExporting(false);
    }
  }

  // Absent, not disabled, where a section has nothing to export — a form, a
  // report. A disabled control says "not right now" and invites the reader to
  // work out what would enable it; the honest signal for one that will never
  // apply to this screen is its absence.
  const exportButton = sectionUi?.exportRows ? (
    <Button
      size="sm"
      variant="outline"
      disabled={exporting}
      onClick={() => {
        void exportCsv();
      }}
    >
      <Download />
      {exporting ? "Exporting…" : "Export"}
    </Button>
  ) : undefined;

  return (
    <PageContainer
      // The section names the page now that the sidebar names the sections
      // (#346). The module goes above it, except where it would say the same
      // thing twice.
      title={section.label}
      count={resultCount}
      breadcrumb={module.sections.length > 1 ? module.label : undefined}
      padded={sectionUi?.padded}
      // On a phone Export is a rare action, so it moves into the list's "⋯"
      // menu and leaves the header to the title (#643).
      actions={
        <>
          {isMobile && hasFields ? null : exportButton}
          {/* The screen's primary action — "New contact" — portals in here. */}
          <span ref={setActionSlot} className="contents" />
        </>
      }
      toolbar={
        /*
          A section that declares no fields has no list to control — the
          dashboard, GST's HSN summary and exports. It used to render the row
          anyway with all four controls disabled, four dead things above a
          screen of cards.

          One bar rather than a toolbar over a tab row over a chip row
          (#349): the view picker, the search-and-chips field, and the
          controls that used to sit beside Filter all read left to right in
          one place now, and everything it needs — filter, sort, columns,
          the saved-view scope — is already state this page owns for the
          screen below it.
        */
        hasFields ? (
          // A report's toolbar sits behind one "Filters" button (#639).
          <CollapsedFilters
            enabled={sectionUi?.report === true}
            filter={filter}
            search={search.q}
          >
            <FilterBar
              scope={viewScope}
              emptyLabel={`All ${subject}`}
              placeholder={`Search ${subject}`}
              listControls={!sectionUi?.report}
              fields={allFields}
              common={common}
              countOf={countOf}
              moreActions={isMobile ? exportButton : undefined}
              toolsSlot={setToolsSlot}
              showLabel={
                resultCount === null
                  ? undefined
                  : `Show ${resultCount.toLocaleString("en-IN")} ${listState.things}`
              }
              filter={filter}
              search={search.q}
              sort={sort}
              columns={columns}
              layout={layout}
              layouts={layouts}
              onChangeFilter={(next) => {
                apply({ filter: next });
              }}
              onChangeSearch={(next) => {
                apply({ q: next ?? null });
              }}
              onClearAll={() => {
                apply({ filter: emptyFilter(), q: null });
              }}
              onChangeSort={(next) => {
                apply({ sort: next });
              }}
              onChangeColumns={(next) => {
                apply({ columns: next });
              }}
              onChangeLayout={(next) => {
                writeLayoutPreference(here, next);
                apply({ view: next });
              }}
              onSelectView={(view) => {
                apply(
                  view
                    ? {
                        filter: view.filter,
                        columns: view.columns,
                        sort: view.sort,
                      }
                    : {
                        filter: emptyFilter(),
                        columns: defaultColumns(allFields),
                        sort: [],
                      },
                );
              }}
            />
          </CollapsedFilters>
        ) : undefined
      }
    >
      {/*
        A hidden nav entry is a courtesy; its URL is still typeable, and until
        M2-15 typing it rendered the page. Switching the persona to Warehouse
        and asking for /settings/roles showed the role builder.

        Refused rather than redirected, and this is still not enforcement —
        `requirePermission()` on the server is (FR-ACL-8), and it runs whatever
        this does.
      */}
      {missingPermission !== undefined ? (
        <NoAccess permission={missingPermission} />
      ) : Screen ? (
        <ResultSetContext value={resultSet}>
          <FilterProblemsContext value={filterProblems}>
            <ListStateContext value={listState}>
              <SectionActionsContext value={actionSlot}>
                <ListToolsSlotContext value={toolsSlot}>
                  <Screen
                    fields={applyColumns(allFields, columns)}
                    allFields={allFields}
                    filter={filter}
                    search={search.q}
                    sort={sort}
                    layout={layout}
                    onToggleSort={(field, additive) => {
                      apply({ sort: toggleSort(sort, field, additive) });
                    }}
                    // The filter and the typed term in one navigation. A list that
                    // matched nothing for "zzz" and offered "Clear filters" gave a
                    // button that appeared to do nothing, since the filter was never
                    // what excluded the rows (#503). One `apply`, not two: a second
                    // navigation would be built from the filter the first one cleared.
                    onClearFilter={() => {
                      apply({ filter: emptyFilter(), q: null });
                    }}
                    onChangeFilter={(next) => {
                      apply({ filter: next });
                    }}
                  />
                </ListToolsSlotContext>
              </SectionActionsContext>
            </ListStateContext>
          </FilterProblemsContext>
        </ResultSetContext>
      ) : (
        <EmptyState
          title="Not built yet"
          description={`${section.label} gets its screen in the module wave. Orders is wired — open it to see the shared list working.`}
        />
      )}
    </PageContainer>
  );
}
